import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';

interface LoginFormProps {
  onSuccess?: () => void;
  onShowContactAnimation?: (show: boolean) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onShowContactAnimation }) => {
  const { loginWithEmail, checkUserSignInMethods, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showContactAnimation, setShowContactAnimation] = useState(false);

  // Función para validar formato de email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPasswordClick = () => {
    navigate('/reset-password');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setShowContactAnimation(false);
    
    // Validar formato de email primero
    if (!isValidEmail(email)) {
      setLocalError('Por favor, ingresá un email válido.');
      return;
    }
    
    try {
      // Intentar login directo primero
      console.log('Intentando login para:', email);
      await loginWithEmail(email, password);
      onSuccess?.();
    } catch (e: any) {
      console.error('Error en login:', e);
      
      // Manejar errores específicos
      if (e?.message?.includes('user-not-found')) {
        setLocalError('Usuario no encontrado. Verificá tu cuenta de email o contactá al equipo de Zeta AI para afiliarte.');
        setShowContactAnimation(true);
        onShowContactAnimation?.(true);
      } else if (e?.message?.includes('wrong-password') || e?.message?.includes('invalid-credential')) {
        setLocalError('Contraseña incorrecta.');
      } else if (e?.message?.includes('too-many-requests')) {
        setLocalError('Demasiados intentos fallidos. Esperá un momento e intentá nuevamente.');
      } else if (e?.message?.includes('user-disabled')) {
        setLocalError('Tu cuenta está deshabilitada. Contactá al equipo de Zeta AI.');
      } else {
        // Si hay otro error, intentar verificar métodos de login como fallback
        try {
          console.log('Verificando métodos de login como fallback...');
          const signInMethods = await checkUserSignInMethods(email);
          console.log('Métodos encontrados:', signInMethods);
          
          if (signInMethods.length === 0) {
            setLocalError('Usuario no encontrado. Verificá tu cuenta de email o contactá al equipo de Zeta AI para afiliarte.');
            setShowContactAnimation(true);
            onShowContactAnimation?.(true);
          } else if (signInMethods.includes('google.com')) {
            setLocalError('Tu cuenta solo está vinculada con Google. Usá el botón "Iniciar sesión con Google" para acceder.');
          } else {
            setLocalError('Error al iniciar sesión. Contactá al equipo de Zeta AI si el problema persiste.');
          }
        } catch (fallbackError) {
          console.error('Error en verificación de métodos:', fallbackError);
          setLocalError('Error al iniciar sesión. Contactá al equipo de Zeta AI si el problema persiste.');
        }
      }
    }
  };

  return (
    <motion.form onSubmit={handleSubmit} className="space-y-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1 text-left">{t('email')}</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="input"
          placeholder={t('emailPlaceholder')}
        />
      </div>
      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1 text-left">{t('password')}</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="input pr-10"
            placeholder={t('passwordPlaceholder')}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {(localError) && (
        <div className="text-sm text-red-600 dark:text-red-400">{localError}</div>
      )}
      
      {/* Botón de "¿Olvidaste tu contraseña?" - siempre visible */}
      <div className="text-center">
        <button 
          type="button"
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          onClick={handleForgotPasswordClick}
        >
          ¿Olvidaste tu contraseña?
        </button>
      </div>
      
      <button type="submit" className="btn-primary w-full" disabled={loading}>
        {loading ? t('loading') : t('loginTitle')}
      </button>
    </motion.form>
  );
};

export default LoginForm;


